import React, { useEffect, useState } from "react";
import axios from "axios";
import { Url } from "src/services/http";
import {
  Box,
  Container,
  Stack,
  Typography,
  CircularProgress,
  Paper,
  Card,
  Grid,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { DataHelper } from "src/services/dataHelper";
import { Link as RouterLink } from "react-router-dom";
import { paths } from "src/paths";

const PAYMENT_RETURN_CONTEXT_KEY = "inv_payment_return_context";

/** Avoid duplicate decrypt/API work if the effect runs twice (e.g. React Strict Mode). */
let lastProcessedEncResp = null;

function readPaymentReturnContext() {
  try {
    const raw = sessionStorage.getItem(PAYMENT_RETURN_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearPaymentReturnContext() {
  sessionStorage.removeItem(PAYMENT_RETURN_CONTEXT_KEY);
}

export default function PaymentResponse() {
  const [status, setStatus] = useState("Processing...");
  const [details, setDetails] = useState({});
  const [returnContext, setReturnContext] = useState(null);
  const { t } = useTranslation();
  useEffect(() => {
    const handlePayment = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const encResp = params.get("encResp");

        if (!encResp) {
          setStatus("No response ❌");
          return;
        }

        if (lastProcessedEncResp === encResp) {
          return;
        }
        lastProcessedEncResp = encResp;

        const ctx = readPaymentReturnContext();
        setReturnContext(ctx);

        const res = await axios.post(
          `${Url.baseUrl}/payment/response`,
          { encResp }
        );

        const decrypted = res.data.decrypted;

        const obj = {};
        decrypted.split("&").forEach((item) => {
          const [k, v] = item.split("=");
          obj[k] = decodeURIComponent(v || "");
        });

        setDetails(obj);

        // update backend
        await UpdatedPayment(obj, ctx);

        // set UI status
        if (obj.order_status === "Success") {
          setStatus("Payment Successful ✅");
        } else if (obj.order_status === "Failure") {
          setStatus("Payment Failed ❌");
        } else {
          setStatus("Payment Pending ⏳");
        }

        clearPaymentReturnContext();
      } catch (error) {
        console.error(error);
        setStatus("Error ❌");
      }
    };

    handlePayment();
  }, []);

  // backend update
  const UpdatedPayment = async (obj, ctx) => {
    try {

      const dataObj = {
        ...(ctx?.operationHeadId != null && {
          operationHeadId: ctx.operationHeadId,
        }),
        ...(ctx?.selectedAddressId != null && {
          addressId: ctx.selectedAddressId,
        }),
        orderId: obj.order_id,
        orderStatus: 'Confirm',
        action: 'Completed',
        paymentStatus:
          obj.order_status === "Success" ? "Paid" : "Failed",
        amount: obj.amount,
      };
      console.log("dataObj", dataObj)
      await new DataHelper().save("Operations", dataObj);
      const response = await new DataHelper().save('Operations', dataObj);
      console.log("response", response)
    } catch (error) {
      console.log("Update error", error);
    }
  };

  return (
    <>
      <Seo title={t("Payment Response")} />

      <Box sx={{ flexGrow: 1, py: 3 }}>
        <Container maxWidth="sm">
          <Stack spacing={3}>
            {/* STATUS CARD */}
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} mb={2}>
                {t("Payment Response")}
              </Typography>

              {status === "Processing..." ? (
                <Stack spacing={2} alignItems="center">
                  <CircularProgress />
                  <Typography>{status}</Typography>
                </Stack>
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    color:
                      status.includes("Successful")
                        ? "green"
                        : status.includes("Failed") ||
                          status.includes("Error")
                          ? "red"
                          : "orange",
                  }}
                >
                  {status}
                </Typography>
              )}
            </Card>

            {/* DETAILS */}
            {Object.keys(details).length > 0 && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  {t("Order Details")}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      {t("Order ID")}
                    </Typography>
                    <Typography>{details.order_id}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      {t("Amount")}
                    </Typography>
                    <Typography>{details.amount}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      {t("Payment Mode")}
                    </Typography>
                    <Typography>{details.payment_mode}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      {t("Status")}
                    </Typography>
                    <Typography>{details.order_status}</Typography>
                  </Grid>

                  {returnContext?.operationHeadId != null && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">
                        {t("Operation")}
                      </Typography>
                      <Typography>{returnContext.operationHeadId}</Typography>
                    </Grid>
                  )}
                  {returnContext?.selectedAddressId != null && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">
                        {t("Address")}
                      </Typography>
                      <Typography>{returnContext.selectedAddressId}</Typography>
                    </Grid>

                  )}
                  <Grid item xs={12} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="subtitle2">
                      {t("Thank you for your purchase")}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* NAVIGATION BUTTONS */}
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Button
                variant="contained"
                component={RouterLink}
                to={paths.dashboard.index}
              >
                {t("Continue Shopping")}
              </Button>

              <Button
                variant="outlined"
                component={RouterLink}
                to={paths.profile.myOrder}
              >
                {t("My Orders")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
}